import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCBadge,
	RCCTooltip,
	RCCInput
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import FiltersContainer from "src/components/FiltersContainer";
import Loader from "src/components/Loader";
import LovIndicatorFields from "src/components/Lov/IndicatorFields";
import Pagination from "src/components/Pagination";
import { useTranslation } from "src/context/Translations/context";
import formatNumber from "src/utils/formatNumber";
import { css } from "styled-components";
import { IRefValuesLogs, IValuesLogs } from "./interface";

const ValuesImportLogs: React.ForwardRefRenderFunction<IRefValuesLogs,IValuesLogs> = ({iqb004Id,iqc001Id},ref) => { 

	const paginationRef = useRef<IForwardPagination>(null);

	const { translate,currentLanguage } = useTranslation();
	const [filters,setFilters] = useState<{fieldId?:string,filter?:string,iqb004Id:number,iqc001Id:number}>({iqb004Id,iqc001Id});
	const [showFilters,setShowFilters] = useState<boolean>(false);
	const [loading, setLoading] = useState(true); 

	const {
		control:controlFilters,
		register:registerFilters,
		reset:resetFilters,
		handleSubmit:handleSubmitFilters
	}= useForm();
    
	const submitFilters = async(data:any) => {
		setFilters((prevState) => {
			const newState = {} as any;
			if(data.fieldId){
				newState.fieldId = typeof data.fieldId === "object" ? data.fieldId?.value : data.fieldId;
			}
			else{
				newState.fieldId = "";
			}
			
			newState.filter =data.filter;

			return {...prevState,...newState};
		});
		executeQuery();
	};

	const executeQuery = () => {
		setLoading(false);
		paginationRef.current?.reload();
	};

	const colProcess = (row: any) => { 
		
		let statusText;
		switch (row.iql003Operacao) {
		case "A":
			statusText="Alteração";
			break;
		case "E":
			statusText="Exclusão";
			break;
		default:
			statusText="Inserção";
			break;
		}

		return(	
			<RCCBadge 
				color="grey"
				styleInline="font-size: 0.83rem;"
			>
				{translate(statusText)}
			</RCCBadge>
		);
	};

	const colValue = (value:number,decimalPlaces:number) => <span>{formatNumber(value,currentLanguage,decimalPlaces)}</span>;

	useImperativeHandle(ref, () => ({
		executeQuery
	}));

	if(loading)
		return <Loader />;
		
	return (
		<>
			<RCCGrid
				container
				item
			>
				<RCCGrid container justifyContent="flex-end">
					<RCCTooltip
						text={translate("Filtros")}
						color={
							showFilters || 
							filters?.fieldId || 
							filters?.filter ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								showFilters || 
                                filters?.fieldId || 
                                filters?.filter ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</RCCGrid>
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={
						() => resetFilters({
							fieldId: {value:"",label:""},
							filter:""
						})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
							>
								<LovIndicatorFields
									control={controlFilters}
									name="fieldId"
									label={translate("Campos")}
									isClearable
									isCreatable={false}
									extraParametersOnLoad={{Iqc001Id:iqc001Id}}
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={filters?.filter}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="IndicatorsValues/ListValuesLogs"
					filtersData={filters}    
					styleInline={{row: css`
                        >div{
                            >div:last-child{
                                justify-content: unset;
                            }
                        }
                    `}}
					columns={[
						{
							idColumn: "iqb001Decricao",
							nameColumn: translate("Campo"),
							xs:1,
							sm: 4,
							type: "string"
						},
						{
							idColumn: "iql003Datahora",
							nameColumn: translate("Data e hora"),
							xs: 1,
							sm: 2,
							type: "date",
							format:{date:currentLanguage === "pt" ? "dd/MM/yyyy HH:mm":"MM/dd/yyyy HH:mm"}
						},
						{
							idColumn: "iql003Operacao",
							nameColumn: translate("Operação"),
							xs:1,
							sm: 2,
							cellRenderer:colProcess
						},
						{
							idColumn: "efa005Nome",
							nameColumn: translate("Usuário"),
							xs:1,
							sm: 1,
							type: "string"
						},
						{
							idColumn: "iql003Valorold",
							nameColumn: translate("Valor Anterior"),
							xs:1,
							sm: 1,
							cellRenderer:(row:any) => colValue(row.iql003Valorold,row.iqb001Casasdecimais)
						},
						{
							idColumn: "iql003Valornew",
							nameColumn: translate("Valor Novo"),
							xs:1,
							sm: 1,
							cellRenderer:(row:any) => colValue(row.iql003Valornew,row.iqb001Casasdecimais)
						}
					]}
				/>
			</RCCGrid>
		</>
	);
};

export default forwardRef(ValuesImportLogs);
